import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

let activeScrollAnimationFrame;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const currentSignature = this.#getMediaSignature(this);
        const newSignature = this.#getMediaSignature(newMediaGallery);
        if (currentSignature === newSignature) return;

        this.#prioritizeFirstImage(newMediaGallery);

        const shouldScroll = window.matchMedia('(max-width: 749px)').matches;
        this.replaceWith(newMediaGallery);
        this.#animateGalleryIn(newMediaGallery);

        if (shouldScroll) {
          requestAnimationFrame(() => this.#scrollToGallery(newMediaGallery));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * @param {Element} gallery
   * @returns {string}
   */
  #getMediaSignature(gallery) {
    return Array.from(gallery.querySelectorAll('.product-media[data-media-id]'))
      .map((media) => media.getAttribute('data-media-id') || '')
      .join(',');
  }

  /**
   * @param {Element} gallery
   */
  #prioritizeFirstImage(gallery) {
    const image = gallery.querySelector('.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;

    image.loading = 'eager';
    image.fetchPriority = 'high';
    image.decoding = 'async';
  }

  /**
   * @param {Element} gallery
   */
  #animateGalleryIn(gallery) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    gallery.animate(
      [
        { opacity: 0.86 },
        { opacity: 1 },
      ],
      {
        duration: 80,
        easing: 'ease-out',
      }
    );
  }

  /**
   * @param {Element} gallery
   */
  #scrollToGallery(gallery) {
    const header = document.querySelector('header-component');
    const headerHeight = header?.getBoundingClientRect().height || 0;
    const galleryTop = gallery.getBoundingClientRect().top + window.scrollY;
    const targetTop = Math.max(galleryTop - headerHeight - 8, 0);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo({ top: targetTop, behavior: 'auto' });
      return;
    }

    if (activeScrollAnimationFrame) cancelAnimationFrame(activeScrollAnimationFrame);

    const startTop = window.scrollY;
    const distance = targetTop - startTop;
    const duration = Math.min(200, Math.max(120, Math.abs(distance) * 0.14));
    const startedAt = performance.now();

    const animateScroll = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      window.scrollTo(0, startTop + distance * easedProgress);

      if (progress < 1) {
        activeScrollAnimationFrame = requestAnimationFrame(animateScroll);
      } else {
        activeScrollAnimationFrame = undefined;
      }
    };

    activeScrollAnimationFrame = requestAnimationFrame(animateScroll);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   *
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
